import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ChevronLeft, X, Zap, Target, Search, FileSpreadsheet, Eye } from 'lucide-react';

export const StudentReportsContent = ({
  students,
  counselors,
  setActiveSection,
  setSelectedStudent,
  setShowStudentModal
}) => {
  // Filtre state'leri
  const [loginFilter, setLoginFilter] = useState('');
  const [agreementFilter, setAgreementFilter] = useState('');
  const [counselorReportFilter, setCounselorReportFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [levelFilter, setLevelFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showResults, setShowResults] = useState(false);
  
  // İstatistikler
  const neverLoggedIn = students.filter(s => !s.last_login).length;
  const withAgreement = students.filter(s => s.agreements_accepted).length;
  const totalMiles = students.reduce((sum, s) => sum + (s.miles || 0), 0);
  const avgMiles = students.length > 0 ? Math.round(totalMiles / students.length) : 0;
  
  // Son 7 günde aktif olanlar
  const activeLastWeek = students.filter(s => {
    if (!s.last_login) return false;
    const lastLogin = new Date(s.last_login);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return lastLogin >= weekAgo;
  }).length;
  
  // Filtreleme fonksiyonu
  const getFilteredStudents = () => {
    let result = [...students];
    
    if (loginFilter === 'logged_in') {
      result = result.filter(s => s.last_login);
    } else if (loginFilter === 'never_logged_in') {
      result = result.filter(s => !s.last_login);
    }
    
    if (agreementFilter === 'accepted') {
      result = result.filter(s => s.agreements_accepted);
    } else if (agreementFilter === 'not_accepted') {
      result = result.filter(s => !s.agreements_accepted);
    }
    
    if (counselorReportFilter) {
      result = result.filter(s => s.counselor_id === counselorReportFilter);
    }
    
    if (activityFilter === 'active_7') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      result = result.filter(s => s.last_login && new Date(s.last_login) >= weekAgo);
    } else if (activityFilter === 'active_30') {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      result = result.filter(s => s.last_login && new Date(s.last_login) >= monthAgo);
    } else if (activityFilter === 'inactive_30') {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      result = result.filter(s => !s.last_login || new Date(s.last_login) < monthAgo);
    }
    
    if (levelFilter) {
      result = result.filter(s => s.current_level === levelFilter);
    }
    
    if (dateRangeFilter.start) {
      result = result.filter(s => s.created_at && new Date(s.created_at) >= new Date(dateRangeFilter.start));
    }
    if (dateRangeFilter.end) {
      result = result.filter(s => s.created_at && new Date(s.created_at) <= new Date(dateRangeFilter.end));
    }
    
    result.sort((a, b) => {
      let compareA, compareB;
      switch (sortBy) {
        case 'name':
          compareA = a.name?.toLowerCase() || '';
          compareB = b.name?.toLowerCase() || '';
          break;
        case 'miles':
          compareA = a.miles || 0;
          compareB = b.miles || 0;
          break;
        case 'last_login':
          compareA = a.last_login ? new Date(a.last_login).getTime() : 0;
          compareB = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'created_at':
          compareA = a.created_at ? new Date(a.created_at).getTime() : 0;
          compareB = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          compareA = a.name?.toLowerCase() || '';
          compareB = b.name?.toLowerCase() || '';
      }
      
      if (sortOrder === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });
    
    return result;
  };
  
  const filteredStudents = getFilteredStudents();
  
  const quickReports = [
    { id: 'never_logged', label: 'Hiç Giriş Yapmamış', count: neverLoggedIn, color: 'red', filter: () => { setLoginFilter('never_logged_in'); setShowResults(true); } },
    { id: 'no_agreement', label: 'Sözleşme Bekleyen', count: students.filter(s => !s.agreements_accepted).length, color: 'amber', filter: () => { setAgreementFilter('not_accepted'); setShowResults(true); } },
    { id: 'inactive_30', label: 'Son 30 Gün Pasif', count: students.filter(s => { const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30); return !s.last_login || new Date(s.last_login) < monthAgo; }).length, color: 'orange', filter: () => { setActivityFilter('inactive_30'); setShowResults(true); } },
    { id: 'active_7', label: 'Son 7 Gün Aktif', count: activeLastWeek, color: 'green', filter: () => { setActivityFilter('active_7'); setShowResults(true); } },
  ];
  
  const clearFilters = () => {
    setLoginFilter('');
    setAgreementFilter('');
    setCounselorReportFilter('');
    setActivityFilter('');
    setDateRangeFilter({ start: '', end: '' });
    setLevelFilter('');
    setSortBy('name');
    setSortOrder('asc');
    setShowResults(false);
  };
  
  const activeFilterCount = [loginFilter, agreementFilter, counselorReportFilter, activityFilter, levelFilter, dateRangeFilter.start, dateRangeFilter.end].filter(f => f).length;

  const handleExportCSV = () => {
    const csv = [
      ['İsim', 'E-posta', 'Telefon', 'Seviye', 'Mil', 'Son Giriş', 'Sözleşme'].join(','),
      ...filteredStudents.map(s => [
        s.name,
        s.email,
        s.phone || '-',
        s.current_level || '-',
        s.miles || 0,
        s.last_login ? new Date(s.last_login).toLocaleDateString('tr-TR') : 'Hiç',
        s.agreements_accepted ? 'Evet' : 'Hayır'
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ogrenci_raporu_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Öğrenci Raporları</h2>
            <p className="text-gray-500">Filtrelere göre öğrenci listesi oluşturun</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <Button variant="outline" onClick={clearFilters}>
            <X className="w-4 h-4 mr-2" />
            Filtreleri Temizle ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Özet İstatistikler */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Toplam Öğrenci</p>
            <p className="text-3xl font-bold">{students.length}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-sm">Son 7 Gün Aktif</p>
            <p className="text-3xl font-bold">{activeLastWeek}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <p className="text-purple-100 text-sm">Sözleşme Onaylı</p>
            <p className="text-3xl font-bold">{withAgreement}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
          <CardContent className="p-4">
            <p className="text-amber-100 text-sm">Ortalama Mil</p>
            <p className="text-3xl font-bold">{avgMiles}</p>
          </CardContent>
        </Card>
      </div>

      {/* Hızlı Rapor Kartları */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Hızlı Raporlar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickReports.map(report => (
              <button
                key={report.id}
                onClick={report.filter}
                className={`p-4 rounded-xl border-2 transition-all hover:scale-105 ${
                  report.color === 'red' ? 'border-red-200 bg-red-50 hover:bg-red-100' :
                  report.color === 'amber' ? 'border-amber-200 bg-amber-50 hover:bg-amber-100' :
                  report.color === 'orange' ? 'border-orange-200 bg-orange-50 hover:bg-orange-100' :
                  'border-green-200 bg-green-50 hover:bg-green-100'
                }`}
              >
                <p className={`text-3xl font-bold ${
                  report.color === 'red' ? 'text-red-600' :
                  report.color === 'amber' ? 'text-amber-600' :
                  report.color === 'orange' ? 'text-orange-600' :
                  'text-green-600'
                }`}>{report.count}</p>
                <p className="text-sm text-gray-600 mt-1">{report.label}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Gelişmiş Filtreler */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            Gelişmiş Filtreler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Giriş Durumu</label>
              <select
                value={loginFilter}
                onChange={(e) => setLoginFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tümü</option>
                <option value="logged_in">Giriş Yapmış</option>
                <option value="never_logged_in">Hiç Giriş Yapmamış</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Sözleşme Durumu</label>
              <select
                value={agreementFilter}
                onChange={(e) => setAgreementFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tümü</option>
                <option value="accepted">Kabul Etmiş</option>
                <option value="not_accepted">Kabul Etmemiş</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Aktivite</label>
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tümü</option>
                <option value="active_7">Son 7 Gün Aktif</option>
                <option value="active_30">Son 30 Gün Aktif</option>
                <option value="inactive_30">Son 30 Gün Pasif</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Danışman</label>
              <select
                value={counselorReportFilter}
                onChange={(e) => setCounselorReportFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tüm Danışmanlar</option>
                {counselors.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Seviye</label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tüm Seviyeler</option>
                <option value="A0">A0</option>
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
                <option value="C1">C1</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Kayıt Tarihi (Başlangıç)</label>
              <Input
                type="date"
                value={dateRangeFilter.start}
                onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                className="h-10"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Kayıt Tarihi (Bitiş)</label>
              <Input
                type="date"
                value={dateRangeFilter.end}
                onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                className="h-10"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Sıralama</label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="name">İsim</option>
                  <option value="miles">Mil</option>
                  <option value="last_login">Son Giriş</option>
                  <option value="created_at">Kayıt Tarihi</option>
                </select>
                <button
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="h-10 w-10 border border-gray-200 rounded-lg bg-white flex items-center justify-center hover:bg-gray-50"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <span className="text-sm text-gray-500">
              <span className="font-semibold text-blue-600">{filteredStudents.length}</span> / {students.length} öğrenci filtreye uyuyor
            </span>
            <Button 
              onClick={() => setShowResults(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Search className="w-4 h-4 mr-2" />
              Raporu Göster
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sonuç Listesi */}
      {showResults && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Rapor Sonuçları ({filteredStudents.length} öğrenci)</span>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV İndir
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Filtrelere uygun öğrenci bulunamadı</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Öğrenci</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Seviye</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Mil</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Son Giriş</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Durum</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(student => (
                      <tr key={student.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm">
                              {student.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{student.name}</p>
                              <p className="text-xs text-gray-500">{student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {student.current_level || 'A0'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="font-medium">{student.miles || 0}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-gray-600">
                            {student.last_login 
                              ? new Date(student.last_login).toLocaleDateString('tr-TR')
                              : <span className="text-red-500">Hiç</span>}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {student.agreements_accepted ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Sözleşme ✓</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Sözleşme ✗</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowStudentModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StudentReportsContent;
